import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Brain, Search, Loader2, Trash2, MessageSquare,
  Lightbulb, Zap, Eye, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const TYPE_CONFIG = {
  brain_decision: { label: 'Decision', color: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400', icon: Zap },
  interaction: { label: 'Interaction', color: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400', icon: MessageSquare },
  strategy: { label: 'Strategy', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', icon: Lightbulb },
  observation: { label: 'Observation', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: Eye },
};

export default function BrainMemoryPanel({ leadId, leadName, refreshKey }) {
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchMemories = useCallback(async () => {
    if (!leadId) return;
    try {
      const res = await api.get(`/api/brain/memory/lead/${leadId}`);
      setMemories(res.data.memories || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [leadId]);

  useEffect(() => { fetchMemories(); }, [fetchMemories, refreshKey]);

  const handleSearch = async () => {
    if (!searchQuery.trim() || searchQuery.length < 3) return;
    setSearching(true);
    try {
      const res = await api.get(`/api/brain/memory/search?q=${encodeURIComponent(searchQuery)}&entity_id=${leadId}&limit=5`);
      setSearchResults(res.data.results || []);
    } catch { toast.error('Search failed'); }
    finally { setSearching(false); }
  };

  const handleDelete = async (memoryId) => {
    try {
      await api.delete(`/api/brain/memory/${memoryId}`);
      setMemories(prev => prev.filter(m => m.id !== memoryId));
      toast.success('Memory removed');
    } catch { toast.error('Failed to delete'); }
  };

  const displayMemories = searchResults || memories;
  const isSearch = searchResults !== null;
  const hasMemories = memories.length > 0;

  return (
    <Card data-testid="brain-memory-panel" className="border-indigo-500/20 dark:border-indigo-500/10">
      <CardHeader className="pb-2 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center">
              <Brain className="h-3.5 w-3.5 text-white" />
            </div>
            Brain Memory
            {hasMemories && (
              <Badge variant="secondary" className="text-[10px] h-4 px-1.5">
                {memories.length}
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            {hasMemories && (
              <span className="text-[10px] text-muted-foreground">
                {memories.length} memor{memories.length === 1 ? 'y' : 'ies'}
              </span>
            )}
            {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0" data-testid="brain-memory-content">
          {/* Search bar */}
          <div className="flex gap-2 mb-3">
            <Input
              placeholder="Search memories..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (!e.target.value) setSearchResults(null);
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="h-8 text-xs"
              data-testid="brain-memory-search"
            />
            <Button
              variant="outline" size="sm"
              onClick={handleSearch}
              disabled={searching || searchQuery.length < 3}
              className="h-8 px-3 shrink-0"
              data-testid="brain-memory-search-btn"
            >
              {searching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
            </Button>
          </div>

          {isSearch && (
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] text-muted-foreground">
                {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{searchQuery}"
              </span>
              <button
                onClick={() => { setSearchResults(null); setSearchQuery(''); }}
                className="text-[10px] text-indigo-600 hover:underline"
              >
                Clear
              </button>
            </div>
          )}

          {loading ? (
            <div className="py-4 flex justify-center">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : displayMemories.length === 0 ? (
            <div className="text-center py-4">
              <Brain className="h-8 w-8 text-indigo-200 mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">
                {isSearch ? 'No matching memories' : 'No memories yet — the Brain will build context as it interacts with this lead'}
              </p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="brain-memory-list">
              {displayMemories.slice(0, 10).map((mem) => {
                const conf = TYPE_CONFIG[mem.memory_type] || TYPE_CONFIG.observation;
                const Icon = conf.icon;
                return (
                  <div
                    key={mem.id}
                    className="group relative rounded-lg border border-border/50 p-2.5 hover:border-indigo-200 transition-colors bg-card"
                    data-testid={`brain-memory-${mem.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <Icon className="h-3.5 w-3.5 text-indigo-500 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                          <Badge className={`text-[9px] h-3.5 px-1 ${conf.color}`}>{conf.label}</Badge>
                          {mem.similarity && (
                            <Badge variant="outline" className="text-[9px] h-3.5 px-1">
                              {Math.round(mem.similarity * 100)}% match
                            </Badge>
                          )}
                          <span className="text-[9px] text-muted-foreground">
                            {new Date(mem.created_at).toLocaleDateString()}
                          </span>
                          {mem.importance >= 0.8 && (
                            <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 text-[9px] h-3.5 px-1">Important</Badge>
                          )}
                        </div>
                        <p className="text-xs leading-relaxed text-foreground/90">{mem.content}</p>
                        {mem.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {mem.tags.slice(0, 4).map(tag => (
                              <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button
                        variant="ghost" size="sm"
                        className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0 text-muted-foreground hover:text-red-600 shrink-0"
                        onClick={() => handleDelete(mem.id)}
                        data-testid={`delete-memory-${mem.id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
